import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useGroupUuid } from "../hooks/use-group-uuid";
import {
  groupKitControllerCreateKit,
  groupKitSkillControllerCreate,
  groupKitSkillLinkControllerLink,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

type CreateSkillOptions = {
  name?: string;
  createGroupAcl?: boolean;
  category?: string | null;
  content?: string;
};

export function useCreateSkillAction() {
  const groupUuid = useGroupUuid();
  const [isCreatingSkill, setIsCreatingSkill] = useState(false);
  const queryClient = useQueryClient();

  async function create({
    name,
    createGroupAcl = false,
    category,
    content,
  }: CreateSkillOptions): Promise<{
    kitUuid: string;
    skillUuid: string;
  }> {
    setIsCreatingSkill(true);

    try {
      if (!groupUuid) {
        throw new Error("Group UUID is required");
      }

      const kit = await groupKitControllerCreateKit({
        path: {
          groupUuid,
        },
        body: {
          name: name ?? "Untitled Skill",
          type: "skill",
        },
      }).then((res) => res.data);

      if (!kit) {
        throw new Error("Failed to create skill");
      }

      // Create the empty skill document
      const skill = await groupKitSkillControllerCreate({
        path: {
          groupUuid,
          kitUuid: kit.uuid,
        },
        body: {
          name: name ?? "Untitled Skill",
          description: "",
          content: content ?? "",
          category: category,
        },
      }).then((res) => res.data);

      if (!skill) {
        throw new Error("Failed to create skill document");
      }

      // Create group ACL if requested
      if (createGroupAcl) {
        await groupKitSkillLinkControllerLink({
          path: {
            groupUuid,
            kitUuid: kit.uuid,
            skillUuid: skill.uuid,
          },
        });
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.kits(groupUuid!, {
          type: "skill",
        }),
      });

      // Invalidate group skills if ACL was created
      if (createGroupAcl) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.groupSkills({
            groupUuid: groupUuid!,
          }),
        });
      }

      toast.success("Skill created successfully!");

      return {
        kitUuid: kit.uuid,
        skillUuid: skill.uuid,
      };
    } catch (error) {
      console.error(error);
      toast.error("Failed to create skill", {
        description: error instanceof Error ? error.message : "",
      });
      throw error;
    } finally {
      setIsCreatingSkill(false);
    }
  }

  return {
    create,
    isCreatingSkill,
  };
}
